import React, { useEffect } from 'react';
import { FaOptionIcon, plusIcon } from '@europrocurement/l2d-icons';
import { Box, CircularProgress, Typography } from '@mui/material';
import { FlexyButton } from '../../atoms/FlexyButton';
import { FlexyTextField } from '../../atoms/FlexyTextField';
import { ObjectGroupProps, ObjectGroupType } from '../../atoms/ObjectGroup';
import { ObjectGroupList } from '../../molecules';
import './ObjectGroupsModal.css';
import { CenterCircularProgress } from '../CenterCircularProgress/CenterCircularProgress';

export type ObjectGroupsModalProps = {
    title: string;
    createButtonLabel: string;
    submitButtonLabel: string;
    placeholderLabel?: string;
    objectGroups: Array<ObjectGroupType>;
    loading?: boolean;
    multiple?: boolean;
    enableSubmit?: boolean;
    submitting?: boolean;
    selectedIds?: Array<number>;
    onCreate?: () => void;
    onSubmit: (ObjectGroup?: ObjectGroupType | Array<ObjectGroupType>) => void;
    onClick?: (objectGroup: ObjectGroupType) => void;
    search: (string: string) => void;
};
export const ObjectGroupsModal: React.FunctionComponent<ObjectGroupsModalProps> = function ({
    title,
    createButtonLabel,
    submitButtonLabel,
    placeholderLabel,
    objectGroups,
    loading = false,
    multiple = false,
    enableSubmit = true,
    submitting = false,
    selectedIds,
    onCreate,
    onSubmit,
    onClick,
    search,
}) {
    const [searchValue, setSearchValue] = React.useState<string>('');
    const [selected, setSelected] = React.useState<Array<ObjectGroupType>>([]);

    useEffect(() => {
        if (undefined === selectedIds) {
            return;
        }

        const tmp: Array<ObjectGroupType> = objectGroups.filter((objectGroup) =>
            selectedIds.includes(objectGroup.id),
        );

        setSelected(tmp);
    }, [objectGroups, selectedIds]);

    const searchWrapper: React.ChangeEventHandler<HTMLInputElement> = (searchString) => {
        search(searchString.target.value);
        setSearchValue(searchString.target.value);
    };

    const onCreateWrapper = () => {
        if (onCreate) {
            onCreate();
        }
    };

    const onClickWrapper: ObjectGroupProps['onClick'] = (objectGroup) => {
        const tmp = [...selected];
        const hasItem = tmp.find((item) => item.id === objectGroup.id) !== undefined;

        // // condition pour déselectionner
        if (multiple) {
            if (hasItem) {
                const idx = tmp.findIndex((item) => item.id === objectGroup.id);
                tmp.splice(idx, 1);
            } else {
                tmp.push(objectGroup);
            }

            setSelected(tmp);
        } else if (hasItem) {
            setSelected([]);
        } else {
            setSelected([objectGroup]);
        }

        if (onClick) {
            onClick(objectGroup);
        }
    };

    const onSubmitWrapper = () => {
        if (multiple) {
            onSubmit(selected);
        } else {
            onSubmit(selected.find(() => true));
        }
    };

    return (
        <Box className="groupment-modal">
            <Box className="body">
                <Box
                    sx={{
                        width: '100%',
                    }}
                >
                    <Box className="title">{title}</Box>
                    <Box sx={{ width: '100%', borderRadius: '10px' }}>
                        <FlexyTextField
                            className="search"
                            variant="outlined"
                            value={searchValue}
                            onChange={searchWrapper}
                            placeholder={placeholderLabel || 'Rechercher'}
                            sx={{ width: '100%' }}
                            inputMode="search"
                            InputProps={{ sx: { borderRadius: '10px' } }}
                        />
                    </Box>
                </Box>
                <Box
                    sx={{
                        width: '100%',
                    }}
                >
                    {!loading && (
                        <ObjectGroupList
                            selected={selected.map((item) => item.id)}
                            objectGroups={objectGroups}
                            onClick={onClickWrapper}
                        />
                    )}
                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', margin: '20px 0 0' }}>
                            <CircularProgress />
                        </Box>
                    )}
                </Box>
            </Box>
            <Box className="actions">
                <FlexyButton
                    className="plus-btn"
                    variant="text"
                    color="primary"
                    onClick={onCreateWrapper}
                >
                    <FaOptionIcon
                        {...plusIcon.props}
                        sx={{ lineHeight: '0' }}
                    />
                    <Typography className="plus-label">{createButtonLabel}</Typography>
                </FlexyButton>
                <FlexyButton
                    type="submit"
                    className="submit-btn"
                    variant="contained"
                    color="primary"
                    onClick={onSubmitWrapper}
                    disabled={!enableSubmit || submitting}
                    sx={{ minWidth: '100px', gap: 2 }}
                >
                    {submitButtonLabel || 'Déplacer'}
                    {submitting ? (
                        <CenterCircularProgress
                            sx={{ scale: 0.5, margin: '-10px', padding: '0px' }}
                        />
                    ) : null}
                </FlexyButton>
            </Box>
        </Box>
    );
};
